/* Result CSVs do not agree on a shape for the Date column. The sim writes
 * "MM/DD/YYYY HH:mm" and public/csvParser.worker.js hands that column through
 * as a raw string; other producers write ISO "YYYY-MM-DD HH:mm:ss". Reading a
 * period out of the stamp by fixed offset only happens to work for the ISO
 * shape — slice(0, 4) of "01/01/2040" is "01/0", which then matches Jan 1-9 and
 * renders nine days of a year view without looking broken — so the period
 * selectors parse the stamp instead.
 *
 * Everything here is pure string work. Round-tripping a stamp through
 * `new Date()` and back out through the local getters is what puts a day-long
 * hole in the data: "2024-01-01" parses as UTC midnight and reads back as
 * 2023-12-31 in any negative-offset zone, moving a reading into the previous
 * year. These stamps are wall-clock simulation time with no zone attached, so
 * they are read and rewritten as wall clock and never resolved against one. */

const ISO_DATE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
/* Trailing zone designators are matched so they can be ignored: see above. */
const TIME = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(?:Z|[+-]\d{2}:?\d{2})?$/;

const pad = (value) => String(value).padStart(2, "0");

/**
 * Splits a result-CSV Date stamp into the period keys the daily/monthly/yearly
 * views select on, plus its wall-clock time. Keys are ISO whatever the stamp's
 * shape, which is also what <input type="date"> and <input type="month">
 * require of their value bounds.
 *
 * @param {string} stamp - "01/01/2040 00:00" or "2040-01-01 00:00:00"; the time
 *   part is optional and may be separated by a space or a "T".
 * @returns {{year: string, month: string, day: string, time: string | null} | null}
 *   e.g. { year: "2040", month: "2040-01", day: "2040-01-01", time: "00:00:00" }.
 *   `time` is "00:00:00" for a date with no time part and null when a time part
 *   is present but unreadable — the date keys are still good in that case, so
 *   the row can be placed in a period even though it cannot be stamped.
 *   Returns null for a stamp whose date cannot be read at all, so callers drop
 *   the row rather than group it under a mis-read key.
 */
export function parseSimulationDate(stamp) {
  if (typeof stamp !== "string") return null;

  const trimmed = stamp.trim();
  const separator = trimmed.search(/[ T]/);
  const datePart = separator === -1 ? trimmed : trimmed.slice(0, separator);
  const timePart = separator === -1 ? "" : trimmed.slice(separator + 1).trim();
  if (!datePart) return null;

  let year;
  let month;
  let day;

  const iso = ISO_DATE.exec(datePart);
  if (iso) {
    [, year, month, day] = iso;
  } else {
    const us = US_DATE.exec(datePart);
    if (!us) return null;
    [, month, day, year] = us;
  }

  /* Guards the shape, not the calendar: "13/32/2040" is a stamp this parser
   * has misread rather than a date, while Feb 30 is the writer's problem and
   * still groups under the month the writer meant. */
  if (+month < 1 || +month > 12 || +day < 1 || +day > 31) return null;

  let time = "00:00:00";
  if (timePart) {
    const parsed = TIME.exec(timePart);
    time =
      parsed && +parsed[1] < 24
        ? `${pad(parsed[1])}:${parsed[2]}:${parsed[3] ?? "00"}`
        : null;
  }

  const isoMonth = `${year}-${pad(month)}`;
  return { year, month: isoMonth, day: `${isoMonth}-${pad(day)}`, time };
}

/**
 * Rewrites a result-CSV Date stamp as "YYYY-MM-DD HH:mm:ss", the shape the rest
 * of the app reads. Pure string work, so a stamp survives the trip unshifted
 * whichever shape it arrived in.
 *
 * @param {string} stamp
 * @returns {string} the normalised stamp, or "" when the stamp cannot be read —
 *   callers decide what to do with an unreadable one rather than having a
 *   blank forced on them.
 */
export function formatSimulationStamp(stamp) {
  const parsed = parseSimulationDate(stamp);
  if (!parsed || parsed.time === null) return "";
  return `${parsed.day} ${parsed.time}`;
}
